import { PRODUCT_GROUP_LABELS } from '../../scorecard/salesTargetsShared';
import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';
import { formatINRCompact } from './_dashboardShared';
import type { TeamSalesData } from './salesDashboardData';

function Section({ title, to, children }: { title: string; to: string; children: ReactNode }) {
  return <section className="card min-w-0 p-4">
    <div className="mb-4 flex items-center justify-between gap-3">
      <h3 className="text-sm font-semibold">{title}</h3>
      <Link className="text-xs underline" to={to} aria-label={`View ${title}`}>View all</Link>
    </div>
    {children}
  </section>;
}
const dateLabel = (date: string) => new Date(date).toLocaleString('en-IN', {
  day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata',
});
export function TeamSalesSections({ data }: { data: TeamSalesData }) {
  const max = Math.max(1, ...data.stages.map(stage => stage.count));
  const th = 'px-3 py-2 text-left text-xs font-medium whitespace-nowrap';
  const td = 'px-3 py-3 text-sm';
  return <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
    <Section title="Pipeline by stage" to="/pipeline">
      <div className="space-y-4">
        {data.stages.map(stage => <Link key={stage.id} to="/pipeline" className="block" aria-label={`Open ${stage.name} pipeline`}>
          <div className="mb-1 flex justify-between gap-3 text-xs">
            <span>{stage.name}{stage.period === 'month' ? ` (${data.period.split(' ')[0]})` : ''}</span>
            <span>{stage.count} · {stage.value === null ? 'Value needs review' : formatINRCompact(stage.value)}</span>
          </div>
          <div className="h-2 overflow-hidden rounded bg-stone-100" aria-hidden="true">
            <div className="h-full rounded" style={{ width: `${stage.count / max * 100}%`, background: stage.id === 'won' ? 'var(--forest-600)' : 'var(--saffron)' }} />
          </div>
          {stage.unvalued > 0 && <p className="mt-1 text-xs text-amber-800">{stage.unvalued} without an open quote</p>}
        </Link>)}
        {data.stages.length === 0 && <p className="text-sm text-stone-500">No pipeline stages configured.</p>}
      </div>
    </Section>
    <div className="min-w-0 xl:col-span-2">
      <Section title="Rep performance" to="/sales/reps">
        {data.reps.length === 0 ? <p>No team members found.</p> : <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr>{['Rep', 'Open leads', `Won (${data.period.split(' ')[0]})`, 'Target', 'Calls today', 'Overdue'].map(label => <th key={label} className={th}>{label}</th>)}</tr></thead>
            <tbody>{data.reps.map(rep => <tr key={rep.id} className="border-t border-stone-100">
              <td className={td}><Link to="/sales/reps" className="underline">{rep.name}</Link></td>
              <td className={td}>{rep.openDeals}</td>
              <td className={`${td} whitespace-nowrap`}>{rep.won === null ? 'Value needs review' : formatINRCompact(rep.won)}</td>
              <td className={td}>{rep.targetPct === null ? (rep.targetMissing === false ? 'Needs review' : rep.targetsNotSetUp?.length ? 'Not set up' : 'No target set') : `${rep.targetPct.toFixed(1)}%`}
                {!!rep.targetsNotSetUp?.length && <span className="block text-xs text-amber-800">{rep.targetsNotSetUp.map(g => PRODUCT_GROUP_LABELS[g as keyof typeof PRODUCT_GROUP_LABELS] ?? g).join(', ')}: not set up</span>}</td>
              <td className={td}>{rep.callsToday}</td><td className={td}>{rep.overdue}</td>
            </tr>)}</tbody>
          </table>
        </div>}
      </Section>
    </div>
    <Section title="Approvals waiting" to="/discount-requests">
      {data.approvals.length === 0 ? <p className="text-sm text-stone-500">No discount requests waiting for your team.</p> :
        <ul className="divide-y divide-stone-100">{data.approvals.map(row => <li key={row.id} className="py-3">
          <Link to="/discount-requests" className="text-sm font-medium underline">{row.document}{row.customer ? ` · ${row.customer}` : ''}</Link>
          <p className="mt-1 text-xs text-stone-500">{row.requester} · {row.requestedPct}%{row.item ? ` on ${row.item}` : ''}</p>
          <Link to="/discount-requests" className="mt-2 inline-block text-xs underline" aria-label={`Review ${row.document}`}>Review request</Link>
        </li>)}</ul>}
    </Section>
    <div className="min-w-0 xl:col-span-2">
      <Section title="Overdue follow-ups" to="/admin/follow-ups">
        {data.overdue.length === 0 ? <p className="text-sm text-stone-500">No overdue follow-ups.</p> : <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr>{['Lead', 'Owner', 'Due', 'Stage', 'Last touch'].map(label => <th key={label} className={th}>{label}</th>)}</tr></thead>
            <tbody>{data.overdue.slice(0, 5).map(row => <tr key={row.id} className="border-t border-stone-100">
              <td className={td}><Link to="/admin/follow-ups" className="underline">{row.lead}</Link></td>
              <td className={td}>{row.owner}</td><td className={`${td} text-red-700`}>{dateLabel(row.due)}</td>
              <td className={td}>{row.stage}</td><td className={td}>{row.lastTouch ? dateLabel(row.lastTouch) : 'No touch recorded'}</td>
            </tr>)}</tbody>
          </table>
          {data.overdue.length > 5 && <p className="mt-3 text-xs text-stone-500">Showing 5 of {data.overdue.length} overdue follow-ups</p>}
        </div>}
      </Section>
    </div>
  </div>;
}
